import { useState, useMemo } from 'react'
import { Link } from '@inertiajs/react'
import { motion, AnimatePresence } from 'framer-motion'
import SeoHead from '@/components/SeoHead'
import AnimatedNumber from '@/components/AnimatedNumber'
import MarketingFooter from '@/components/MarketingFooter'
import { calculateAmortization, type LoanType } from '@/lib/calculations'

// ─── Animation variants ────────────────────────────────────────────────────────

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
}

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
}

// ─── FAQ data ───────────────────────────────────────────────────────────────────

const faqs = [
  {
    question: 'How many loans can I manage?',
    answer:
      'Free tier supports 2 loans. Solo plan ($29/month) supports up to 5. Pro plan ($49/month) supports up to 25. Fund plan ($99/month) supports unlimited loans for larger portfolios.',
  },
  {
    question: 'Can I mix different loan types?',
    answer:
      'Yes. You can have standard amortizing, interest-only, and balloon loans all in the same account. Each loan calculates independently based on its own structure and terms.',
  },
  {
    question: 'What about borrowers with multiple loans?',
    answer:
      'Each loan is tracked independently with its own amortization schedule and payment history. For tax reporting, LendSolo consolidates interest by borrower \u2014 so if one person has three loans, you get a single interest total for their 1098.',
  },
  {
    question: 'Will I outgrow LendSolo?',
    answer:
      'LendSolo is built for private lenders managing portfolios from $50K to $500K+ across 1 to 25+ loans. The Fund tier ($99/month) supports unlimited loans for larger operations.',
  },
]

// ─── Scaling data ───────────────────────────────────────────────────────────────

const scalingTiers = [
  {
    loans: 2,
    paymentsPerMonth: '2',
    loanTypes: '1',
    borrowers: '2',
    capitalAtRisk: '$200K',
    taxComplexity: 'Simple',
    color: 'emerald',
    label: 'Manageable',
  },
  {
    loans: 5,
    paymentsPerMonth: '5',
    loanTypes: '2\u20133',
    borrowers: '4\u20135',
    capitalAtRisk: '$500K',
    taxComplexity: 'Moderate',
    color: 'amber',
    label: 'Caution',
  },
  {
    loans: 10,
    paymentsPerMonth: '10',
    loanTypes: '3+',
    borrowers: '8\u201310',
    capitalAtRisk: '$1M+',
    taxComplexity: 'Complex',
    color: 'red',
    label: 'Danger',
  },
]

const colorMap: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    badge: 'bg-emerald-100 text-emerald-800',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-800',
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    badge: 'bg-red-100 text-red-800',
  },
}

// ─── Feature cards ──────────────────────────────────────────────────────────────

const features = [
  {
    title: 'One Dashboard, Every Loan',
    description:
      'See your entire portfolio at a glance. Total outstanding principal, payments due this month, overdue amounts, and interest earned \u2014 all in real time.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
  },
  {
    title: 'Per-Loan Amortization',
    description:
      'Each loan gets its own amortization schedule, calculated automatically based on its structure \u2014 standard, interest-only, or balloon.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
  {
    title: 'Payment Tracking That Scales',
    description:
      'Record payments with automatic principal/interest split. Handle partial payments, late fees, and multiple payment methods without breaking a sweat.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
      </svg>
    ),
  },
  {
    title: 'Portfolio-Level Reporting',
    description:
      'Year-end tax reports across all loans and all borrowers. One-click export to PDF, CSV, or QuickBooks. 1098 generation for qualifying borrowers.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
]

// ─── Calculator defaults ────────────────────────────────────────────────────────

interface LoanInput {
  principal: string
  rate: string
  term: string
  type: LoanType
}

const defaultLoanA: LoanInput = { principal: '100000', rate: '10', term: '60', type: 'standard' }
const defaultLoanB: LoanInput = { principal: '150000', rate: '12', term: '36', type: 'interest_only' }

// ─── Component ──────────────────────────────────────────────────────────────────

export default function MultipleLoans() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [loanA, setLoanA] = useState<LoanInput>(defaultLoanA)
  const [loanB, setLoanB] = useState<LoanInput>(defaultLoanB)

  const resultA = useMemo(() => {
    const p = parseFloat(loanA.principal) || 0
    const r = parseFloat(loanA.rate) || 0
    const t = parseInt(loanA.term, 10) || 0
    return calculateAmortization(p, r, t, new Date(), loanA.type)
  }, [loanA])

  const resultB = useMemo(() => {
    const p = parseFloat(loanB.principal) || 0
    const r = parseFloat(loanB.rate) || 0
    const t = parseInt(loanB.term, 10) || 0
    return calculateAmortization(p, r, t, new Date(), loanB.type)
  }, [loanB])

  const faqSchema = faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  }))

  return (
    <>
      <SeoHead
        title="Manage Multiple Private Loans Without Errors | LendSolo"
        description="Scaling from 2 loans to 10? LendSolo gives you a single dashboard for every loan, every payment, every borrower — without the errors that come from manual tracking."
        canonicalPath="/manage-multiple-private-loans"
        schema={{
          '@graph': [
            {
              '@type': 'SoftwareApplication',
              name: 'LendSolo',
              description: 'Manage multiple private loans from a single dashboard without errors',
              url: 'https://lendsolo.com/manage-multiple-private-loans',
              applicationCategory: 'FinanceApplication',
              operatingSystem: 'Web',
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
                description: 'Free for up to 2 loans',
              },
            },
            {
              '@type': 'FAQPage',
              mainEntity: faqSchema,
            },
          ],
        }}
      />

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#0F1419' }}
            >
              <span className="text-emerald-400 font-bold text-sm">LS</span>
            </div>
            <span className="text-lg font-bold text-gray-900">LendSolo</span>
          </Link>
          <nav className="flex items-center gap-3">
            <Link
              href="/blog"
              className="hidden sm:inline-block text-sm text-gray-600 hover:text-gray-900 px-3 py-2"
            >
              Blog
            </Link>
            <Link
              href="/calculators"
              className="hidden sm:inline-block text-sm text-gray-600 hover:text-gray-900 px-3 py-2"
            >
              Calculators
            </Link>
            <Link href="/users/sign_in" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2">
              Sign In
            </Link>
            <Link
              href="/users/sign_up"
              className="text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg transition-colors"
            >
              Get Started
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section
        className="py-20 sm:py-28"
        style={{ background: 'linear-gradient(165deg, #0E4D30 0%, #081C12 100%)' }}
      >
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-display">
            Two Loans Was Easy. Five Is Where Things Break.
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mt-6 max-w-2xl mx-auto">
            More loans means more payment dates, more amortization schedules, more tax complexity, and
            more ways to make expensive mistakes. LendSolo keeps it all clean.
          </p>
          <Link
            href="/users/sign_up"
            className="inline-block bg-[#34D399] hover:bg-emerald-400 text-gray-900 font-semibold px-8 py-3.5 rounded-lg mt-8 transition-colors"
          >
            Start Free &rarr;
          </Link>
        </motion.div>
      </section>

      {/* ── Problem narrative ───────────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-24">
        <motion.div
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeIn}
        >
          <p className="text-lg text-gray-600 leading-[1.8]">
            You didn&rsquo;t need software when you had two loans. Two loans fit in your head. Then you
            added a third and fourth &mdash; different structures, scattered payment dates, one borrower
            three days late. This is the scaling wall every private lender hits between 3 and 7 loans.
            The question isn&rsquo;t whether your system will break &mdash; it&rsquo;s whether
            you&rsquo;ll catch the mistakes.
          </p>
        </motion.div>
      </section>

      {/* ── Scaling math ────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-3xl sm:text-4xl font-bold text-gray-900 text-center font-display mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeIn}
          >
            How Complexity Scales
          </motion.h2>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            {scalingTiers.map((tier) => {
              const c = colorMap[tier.color]
              return (
                <motion.div
                  key={tier.loans}
                  variants={fadeIn}
                  className={`rounded-xl border ${c.border} ${c.bg} p-6`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className={`text-5xl font-bold ${c.text} font-display`}>{tier.loans}</span>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.badge}`}>
                      {tier.label}
                    </span>
                  </div>
                  <p className={`text-sm font-medium ${c.text} mb-4`}>loans</p>

                  <div className="space-y-3 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>Payment dates / mo</span>
                      <span className="font-semibold">{tier.paymentsPerMonth}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Loan types to track</span>
                      <span className="font-semibold">{tier.loanTypes}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Borrowers</span>
                      <span className="font-semibold">{tier.borrowers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Capital at risk</span>
                      <span className="font-semibold">{tier.capitalAtRisk}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax complexity</span>
                      <span className="font-semibold">{tier.taxComplexity}</span>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>

          <motion.div
            className="mt-10 bg-white border border-gray-200 rounded-xl p-6 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeIn}
          >
            <p className="text-lg font-semibold text-gray-900 font-display">
              Complexity doesn&rsquo;t scale linearly &mdash; it compounds.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Embedded calculator ─────────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeIn}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 font-display">
              Try it now &mdash; no signup needed
            </h2>
            <p className="text-gray-500 mt-3">Compare two loans side by side</p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            {[
              { loan: loanA, setLoan: setLoanA, result: resultA, label: 'Loan A' },
              { loan: loanB, setLoan: setLoanB, result: resultB, label: 'Loan B' },
            ].map(({ loan, setLoan, result, label }) => (
              <motion.div
                key={label}
                variants={fadeIn}
                className="border border-gray-200 rounded-xl p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 font-display">{label}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Principal ($)
                    </label>
                    <input
                      type="number"
                      value={loan.principal}
                      onChange={(e) => setLoan({ ...loan, principal: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.25"
                        value={loan.rate}
                        onChange={(e) => setLoan({ ...loan, rate: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Term (months)
                      </label>
                      <input
                        type="number"
                        value={loan.term}
                        onChange={(e) => setLoan({ ...loan, term: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
                    <select
                      value={loan.type}
                      onChange={(e) => setLoan({ ...loan, type: e.target.value as LoanType })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="standard">Standard Amortizing</option>
                      <option value="interest_only">Interest Only</option>
                      <option value="balloon">Balloon</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6 space-y-3 border-t border-gray-100 pt-5">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Monthly Payment</span>
                    <AnimatedNumber
                      value={result.monthlyPayment}
                      prefix="$"
                      className="text-lg font-semibold text-gray-900"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Total Interest</span>
                    <AnimatedNumber
                      value={result.totalInterest}
                      prefix="$"
                      className="text-lg font-semibold text-gray-900"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Total Cost</span>
                    <AnimatedNumber
                      value={result.totalCost}
                      prefix="$"
                      className="text-lg font-semibold text-emerald-700"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="text-center mt-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeIn}
          >
            <p className="text-gray-600 mb-4">Ready to manage them all?</p>
            <Link
              href="/users/sign_up"
              className="inline-block bg-[#34D399] hover:bg-emerald-400 text-gray-900 font-semibold px-8 py-3.5 rounded-lg transition-colors"
            >
              Start Free &rarr;
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Solution features ───────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-3xl sm:text-4xl font-bold text-gray-900 text-center font-display mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeIn}
          >
            Built for Portfolios, Not Single Loans
          </motion.h2>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={fadeIn}
                className="bg-white rounded-xl border border-gray-200 p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 font-display mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Social proof stats ──────────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-24">
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={staggerContainer}
        >
          {[
            { stat: '25 loans', detail: 'managed on one screen' },
            { stat: '3 loan types', detail: 'mixed in one portfolio' },
            { stat: 'Under 2 seconds', detail: 'dashboard load time' },
          ].map((item) => (
            <motion.div
              key={item.stat}
              variants={fadeIn}
              className="text-center border border-gray-200 rounded-xl p-6"
            >
              <p className="text-2xl font-bold text-gray-900 font-display">{item.stat}</p>
              <p className="text-sm text-gray-500 mt-1">{item.detail}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── Related pages ───────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-2xl font-bold text-gray-900 font-display mb-8 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeIn}
          >
            You might also find these helpful
          </motion.h2>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            {[
              {
                href: '/private-loan-tax-tracking',
                title: 'Tax Tracking for Private Loans',
                teaser: 'Your spreadsheet can\u2019t generate a 1098. LendSolo can.',
              },
              {
                href: '/stop-using-spreadsheets-loan-tracking',
                title: 'Stop Using Spreadsheets',
                teaser: 'Your lending spreadsheet was fine. Until it wasn\u2019t.',
              },
            ].map((page) => (
              <motion.div key={page.href} variants={fadeIn}>
                <Link
                  href={page.href}
                  className="block bg-white border border-gray-200 rounded-xl p-6 hover:border-emerald-300 hover:shadow-sm transition-all"
                >
                  <h3 className="text-lg font-semibold text-gray-900 font-display">{page.title}</h3>
                  <p className="text-sm text-gray-500 mt-2">{page.teaser}</p>
                  <span className="text-sm font-medium text-emerald-600 mt-3 inline-block">
                    Read more &rarr;
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-3xl sm:text-4xl font-bold text-gray-900 text-center font-display mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeIn}
          >
            Frequently Asked Questions
          </motion.h2>

          <motion.div
            className="space-y-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                variants={fadeIn}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                >
                  <span className="text-base font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-5 text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────────────── */}
      <section
        className="py-20 sm:py-28"
        style={{ background: 'linear-gradient(165deg, #0E4D30 0%, #081C12 100%)' }}
      >
        <motion.div
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeIn}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-display">
            Stop managing loans in your head.
          </h2>
          <p className="text-lg text-gray-300 mt-4">
            One dashboard. Every loan. Every payment. Every borrower.
          </p>
          <Link
            href="/users/sign_up"
            className="inline-block bg-[#34D399] hover:bg-emerald-400 text-gray-900 font-semibold px-8 py-3.5 rounded-lg mt-8 transition-colors"
          >
            Start Free &rarr;
          </Link>
        </motion.div>
      </section>

      <div className="mt-16">
        <MarketingFooter />
      </div>
    </>
  )
}
